import RegistrationField from 'registration/RegistrationField';
import { FieldTypes } from 'registration/RegistrationUtils';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';

import { Trans, t } from '@lingui/macro';
import { useCheckoutContext } from 'context/CheckoutContext';

// Note about React.useId()
// If this page is ever server side rendered, the ID's may not match if the client side renders a slightly different component tree.
// This shouldn't be an issue since no one will access this page directly, it'll always be client rendered, but if that changes, we should look for an alternative.

// React.useId() is for generating an ID that can be used specifically for accessibility tags

export default function Registration() {
    const {
        registrationForm: form,
        event: { checkoutQuestionMode: mode },
        setRegistrationForm: onFormChange,
    } = useCheckoutContext();

    const handleAttendeeUpdate = (value) => {
        const attendees = form.map((a) => {
            if (a.tempId === value.tempId) {
                return value;
            } else {
                return a;
            }
        });

        onFormChange(attendees);
    };

    return (
        <div className="flex bg-background px-4 pb-16 text-foreground">
            <div className="mx-auto w-full max-w-screen-xl">
                {form.map((attendee) => (
                    <AttendeeForm
                        key={attendee.tempId}
                        attendee={attendee}
                        mode={mode}
                        onAttendeeUpdate={handleAttendeeUpdate}
                    />
                ))}
            </div>
        </div>
    );
}

function AttendeeForm(props) {
    const { attendee, mode, onAttendeeUpdate } = props;

    const requiredForm = attendee.requiredForm;
    const customForm = attendee.customForm;

    // ***** Process Form Value Changes ***** //

    const handleRequiredFormChange = (index, value, optionId) => {
        const newForm = Array.from(requiredForm);

        switch (requiredForm[index].type) {
            case FieldTypes.AGE:
                newForm[index].value = value.replace(/[^0-9]/g, '').slice(0, 3);
                break;

            case FieldTypes.MULTIPLE_CHOICE:
                value === true
                    ? newForm[index].value.add(optionId)
                    : newForm[index].value.delete(optionId);
                break;

            default:
                newForm[index].value = value;
                break;
        }

        newForm[index].errorMsg = null;

        onAttendeeUpdate({ ...attendee, requiredForm: newForm });
    };

    const handleCustomFormChange = (index, value, optionId) => {
        const newForm = Array.from(customForm);

        switch (customForm[index].type) {
            case FieldTypes.AGE:
                newForm[index].value = value.replace(/[^0-9]/g, '').slice(0, 3);
                break;

            case FieldTypes.MULTIPLE_CHOICE:
                value === true
                    ? newForm[index].value.add(optionId)
                    : newForm[index].value.delete(optionId);
                break;

            default:
                newForm[index].value = value;
                break;
        }

        newForm[index].errorMsg = null;

        onAttendeeUpdate({ ...attendee, customForm: newForm });
    };

    const containsRequiredFields =
        requiredForm.length > 0 || customForm.some((f) => Boolean(f.required) === true);

    return (
        <Card className="mt-8 border-solid border-border bg-card text-card-foreground shadow-none">
            {mode === 2 ? (
                <CardHeader className="space-y-1.5">
                    <CardTitle className="text-foreground">{attendee.productName}</CardTitle>
                    <p className="m-0 text-sm text-muted-foreground">
                        {t`Attendee` + ' #' + attendee.tempId}
                    </p>
                </CardHeader>
            ) : <div className='pt-6' />}
            <CardContent>
                {requiredForm.map((field, index) => (
                    <RegistrationField
                        key={index}
                        index={index}
                        onChange={handleRequiredFormChange}
                        {...field}
                    />
                ))}
                {customForm.map((field, index) => (
                    <RegistrationField
                        key={index}
                        index={index}
                        onChange={handleCustomFormChange}
                        {...field}
                    />
                ))}
            </CardContent>
            {containsRequiredFields && (
                <div className="mb-1 ml-4">
                    <p className="m-0 inline whitespace-pre-wrap text-sm">
                        <Trans>Fields marked with an asterisk are required</Trans>
                    </p>
                    <span className="ml-1 inline font-bold text-destructive">*</span>
                </div>
            )}
        </Card>
    );
}
